// More instruments (engine rt/textures.rs): supersaw chords and leads, vocal leads, Reese basses,
// mallets and reeds. (The atmosphere engines play in the atmos kits: atmosKits.ts.) Levels are calibrated like every preset
// (`calibrate-presets.test.ts`). Appended to `SYNTH_PRESETS` in presets.ts.
import type { Envelope } from "./generated/Envelope";
import type { SynthModel } from "./generated/SynthModel";
import type { SynthParams } from "./generated/SynthParams";
import type { SynthPreset } from "./presets";

const p = (model: SynthModel, note: number, o: Partial<SynthParams>): SynthParams => ({
  model,
  note,
  cutoff: 0.5,
  resonance: 0.3,
  env_amount: 0.5,
  env_decay: 800,
  glide_ms: 0,
  detune: 0.5,
  tone: 0.5,
  drive: 0.2,
  ...o,
});
const env = (attack_ms: number, decay_ms: number, sustain: number, release_ms: number): Envelope => ({
  attack_ms,
  decay_ms,
  sustain,
  release_ms,
});

export const MORE_PRESETS: readonly SynthPreset[] = [
  // ---------------------------------------------------------------- Harmony: supersaw chords
  {
    id: "anthem-saws",
    family: "supersaw-chords",
    name: "Anthem Saws",
    role: "harmony",
    flavour: "euphoric",
    params: p("super", 60, {
      cutoff: 0.55,
      resonance: 0.15,
      env_amount: 0.35,
      env_decay: 600,
      detune: 0.65,
      tone: 0.7,
      drive: 0.15,
      level: 0.65,
    }),
    env: env(8, 600, 0.85, 350),
    mode: "gate",
    reverb: 0.45,
    delay: 0.15,
  },
  {
    id: "trance-stack",
    family: "supersaw-chords",
    name: "Trance Stack",
    role: "harmony",
    flavour: "soaring",
    params: p("super", 60, {
      cutoff: 0.48,
      resonance: 0.3,
      env_amount: 0.55,
      env_decay: 300,
      detune: 0.8,
      tone: 0.8,
      drive: 0.1,
      level: 0.917,
    }),
    env: env(4, 400, 0.6, 250),
    mode: "gate",
    reverb: 0.4,
    delay: 0.25,
  },
  {
    id: "velvet-saws",
    family: "supersaw-chords",
    name: "Velvet Saws",
    role: "harmony",
    flavour: "plush",
    alternateOnly: true,
    params: p("super", 60, {
      cutoff: 0.42,
      resonance: 0.1,
      env_amount: 0.2,
      env_decay: 1200,
      detune: 0.5,
      tone: 0.55,
      drive: 0.25,
      level: 0.71,
    }),
    env: env(120, 800, 0.9, 600),
    mode: "gate",
    reverb: 0.5,
    delay: 0.1,
  },
  // ---------------------------------------------------------------- Lead: supersaw and vocal leads
  {
    id: "euphoria-lead",
    family: "supersaw-lead",
    name: "Euphoria Lead",
    role: "lead",
    flavour: "uplifting",
    params: p("super", 72, {
      cutoff: 0.62,
      resonance: 0.2,
      env_amount: 0.35,
      env_decay: 400,
      detune: 0.4,
      tone: 0.45,
      drive: 0.2,
      level: 0.943,
    }),
    env: env(4, 500, 0.8, 220),
    mode: "gate",
    reverb: 0.35,
    delay: 0.3,
  },
  {
    id: "sky-lead",
    family: "supersaw-lead",
    name: "Sky Lead",
    role: "lead",
    flavour: "airy",
    params: p("super", 72, {
      cutoff: 0.58,
      resonance: 0.35,
      env_amount: 0.5,
      env_decay: 250,
      detune: 0.3,
      tone: 0.3,
      drive: 0.1,
      level: 1.153,
    }),
    env: env(3, 350, 0.7, 260),
    mode: "gate",
    reverb: 0.4,
    delay: 0.35,
  },
  {
    id: "hyper-lead",
    family: "supersaw-lead",
    name: "Hyper Lead",
    role: "lead",
    flavour: "blazing",
    alternateOnly: true,
    intense: true,
    params: p("super", 72, {
      cutoff: 0.7,
      resonance: 0.25,
      env_amount: 0.3,
      env_decay: 300,
      detune: 0.55,
      tone: 0.6,
      drive: 0.5,
      level: 0.836,
    }),
    env: env(2, 300, 0.8, 200),
    mode: "gate",
    reverb: 0.3,
    delay: 0.25,
  },
  {
    id: "vox-lead",
    family: "vocal-lead",
    name: "Vox Lead",
    role: "lead",
    flavour: "soulful",
    params: p("choir", 72, {
      cutoff: 0.8,
      env_amount: 0.6,
      tone: 0.5,
      detune: 0,
      drive: 0.2,
      level: 0.973,
    }),
    env: env(25, 500, 0.9, 250),
    mode: "gate",
    reverb: 0.4,
    delay: 0.3,
  },
  {
    id: "siren-vox",
    family: "vocal-lead",
    name: "Siren Vox",
    role: "lead",
    flavour: "bright",
    params: p("choir", 72, {
      cutoff: 1,
      env_amount: 0.75,
      tone: 0.85,
      detune: 0.15,
      drive: 0.15,
      level: 2.196,
    }),
    env: env(30, 500, 0.9, 300),
    mode: "gate",
    reverb: 0.45,
    delay: 0.35,
  },
  {
    id: "hum-vox",
    family: "vocal-lead",
    name: "Hum Vox",
    role: "lead",
    flavour: "intimate",
    alternateOnly: true,
    params: p("choir", 72, {
      cutoff: 0.5,
      env_amount: 0.45,
      tone: 0.05,
      detune: 0.1,
      drive: 0.35,
      level: 2.953,
    }),
    env: env(40, 500, 0.9, 300),
    mode: "gate",
    reverb: 0.4,
    delay: 0.2,
  },
  // ---------------------------------------------------------------- Bass: Reese
  {
    id: "reese-bass",
    family: "reese-bass",
    name: "Reese Bass",
    role: "bass",
    flavour: "menacing",
    params: p("super", 36, {
      cutoff: 0.32,
      resonance: 0.2,
      env_amount: 0.25,
      env_decay: 400,
      detune: 0.35,
      tone: 0.35,
      drive: 0.45,
      level: 1.433,
    }),
    env: env(4, 400, 0.9, 120),
    mode: "gate",
    reverb: 0,
    delay: 0,
  },
  {
    id: "hoover-bass",
    family: "reese-bass",
    name: "Hoover Bass",
    role: "bass",
    flavour: "roaring",
    params: p("super", 36, {
      cutoff: 0.4,
      resonance: 0.3,
      env_amount: 0.4,
      env_decay: 300,
      detune: 0.5,
      tone: 0.55,
      drive: 0.6,
      level: 1.178,
    }),
    env: env(3, 350, 0.85, 120),
    mode: "gate",
    reverb: 0,
    delay: 0,
  },
  {
    id: "dark-reese",
    family: "reese-bass",
    name: "Dark Reese",
    role: "bass",
    flavour: "brooding",
    alternateOnly: true,
    params: p("super", 36, {
      cutoff: 0.26,
      resonance: 0.35,
      env_amount: 0.15,
      env_decay: 600,
      detune: 0.28,
      tone: 0.25,
      drive: 0.3,
      level: 1.624,
    }),
    env: env(6, 400, 0.9, 140),
    mode: "gate",
    reverb: 0,
    delay: 0,
  },
  // ---------------------------------------------------------------- Classics: mallets
  {
    id: "vibraphone",
    family: "mallets",
    name: "Vibraphone",
    role: "classic",
    classic: true,
    flavour: "velvety",
    params: p("mallet", 60, {
      tone: 0.25,
      cutoff: 0.5,
      env_decay: 2600,
      resonance: 0.3,
      detune: 0.45,
      drive: 0,
      level: 1.034,
    }),
    env: env(1, 3000, 1, 900),
    mode: "gate",
    reverb: 0.4,
    delay: 0.15,
  },
  {
    id: "celesta",
    family: "mallets",
    name: "Celesta",
    role: "classic",
    classic: true,
    alternateOnly: true,
    flavour: "dreamy",
    // Felt hammers on steel bars: the glockenspiel's bars struck softly, shorter and rounder.
    params: p("mallet", 72, {
      tone: 0.75,
      cutoff: 0.3,
      env_decay: 1400,
      resonance: 0.25,
      detune: 0,
      drive: 0,
      level: 1.276,
    }),
    env: env(1, 1800, 1, 700),
    mode: "gate",
    reverb: 0.45,
    delay: 0.15,
  },
  {
    id: "glockenspiel",
    family: "mallets",
    name: "Glockenspiel",
    role: "classic",
    classic: true,
    alternateOnly: true,
    flavour: "sparkly",
    params: p("mallet", 84, {
      tone: 0.75,
      cutoff: 0.75,
      env_decay: 2200,
      resonance: 0.1,
      detune: 0,
      drive: 0,
      level: 1.131,
    }),
    env: env(1, 2500, 1, 900),
    mode: "gate",
    reverb: 0.45,
    delay: 0.2,
  },
  // ---------------------------------------------------------------- Classics: reeds
  {
    id: "tenor-sax",
    family: "reeds",
    name: "Tenor Sax",
    role: "classic",
    classic: true,
    flavour: "smoky",
    params: p("reed", 60, {
      tone: 0.9,
      cutoff: 0.55,
      resonance: 0.6,
      env_amount: 0.5,
      env_decay: 120,
      glide_ms: 35,
      drive: 0.35,
      level: 1.544,
    }),
    env: env(12, 400, 0.9, 140),
    mode: "gate",
    reverb: 0.35,
    delay: 0.15,
  },
  {
    id: "alto-sax",
    family: "reeds",
    name: "Alto Sax",
    role: "classic",
    classic: true,
    flavour: "sassy",
    params: p("reed", 72, {
      tone: 1,
      cutoff: 0.65,
      resonance: 0.7,
      env_amount: 0.55,
      env_decay: 100,
      glide_ms: 25,
      drive: 0.25,
      level: 1.556,
    }),
    env: env(10, 400, 0.9, 120),
    mode: "gate",
    reverb: 0.35,
    delay: 0.2,
  },
  {
    id: "clarinet",
    family: "reeds",
    name: "Clarinet",
    role: "classic",
    classic: true,
    flavour: "woody",
    params: p("reed", 60, {
      tone: 0,
      cutoff: 0.5,
      resonance: 0.3,
      env_amount: 0.25,
      env_decay: 150,
      glide_ms: 0,
      drive: 0.1,
      level: 1.535,
    }),
    env: env(15, 400, 0.92, 130),
    mode: "gate",
    reverb: 0.35,
    delay: 0.1,
  },
  {
    id: "growl-sax",
    family: "reeds",
    name: "Growl Sax",
    role: "classic",
    classic: true,
    alternateOnly: true,
    intense: true,
    flavour: "raspy",
    params: p("reed", 60, {
      tone: 0.85,
      cutoff: 0.6,
      resonance: 0.65,
      env_amount: 0.4,
      env_decay: 90,
      glide_ms: 50,
      drive: 0.85,
      level: 1.509,
    }),
    env: env(8, 400, 0.9, 120),
    mode: "gate",
    reverb: 0.3,
    delay: 0.15,
  },
  // ---------------------------------------------------------------- Harmony: warm 70s synths
  // A string machine (sawtooth ensemble with a lush chorus), a polysynth brass swell and a soft
  // polysynth pad: the warm, slightly drifting sound of 70s polyphonic synths.
  {
    id: "string-ensemble",
    family: "seventies-keys",
    name: "String Ensemble",
    role: "harmony",
    flavour: "lush",
    params: p("poly", 60, {
      cutoff: 0.5,
      resonance: 0.05,
      env_amount: 0.1,
      env_decay: 1500,
      tone: 0.8,
      detune: 0.95,
      drive: 0.05,
      level: 1.145,
    }),
    env: env(180, 1500, 0.9, 900),
    mode: "gate",
    reverb: 0.5,
    delay: 0.05,
  },
  {
    id: "analog-brass",
    family: "seventies-keys",
    name: "Analog Brass",
    role: "harmony",
    alternateOnly: true,
    flavour: "brassy",
    params: p("poly", 60, {
      cutoff: 0.34,
      resonance: 0.12,
      env_amount: 0.45,
      env_decay: 450,
      tone: 0.1,
      detune: 0.45,
      drive: 0.15,
      level: 1.537,
    }),
    env: env(45, 600, 0.8, 300),
    mode: "gate",
    reverb: 0.3,
    delay: 0.05,
  },
  {
    id: "warm-poly",
    family: "seventies-keys",
    name: "Warm Poly",
    role: "harmony",
    alternateOnly: true,
    flavour: "warm",
    params: p("poly", 60, {
      cutoff: 0.4,
      resonance: 0.08,
      env_amount: 0.2,
      env_decay: 900,
      tone: 0.35,
      detune: 0.7,
      drive: 0.1,
      level: 1.309,
    }),
    env: env(90, 1200, 0.85, 800),
    mode: "gate",
    reverb: 0.45,
    delay: 0.1,
  },
  // ---------------------------------------------------------------- Harmony: deep house keys
  // The deep house chord: a filtered minor-seventh stab with delay and room, a warm tine e-piano
  // with chorus, and a soft pad.
  {
    id: "deep-chord",
    family: "deep-house-keys",
    name: "Deep Chord",
    role: "harmony",
    flavour: "deep",
    params: p("poly", 60, {
      cutoff: 0.37,
      resonance: 0.22,
      env_amount: 0.35,
      env_decay: 320,
      tone: 0.25,
      detune: 0.55,
      drive: 0.05,
      level: 3.322,
    }),
    env: env(2, 500, 0.15, 350),
    mode: "gate",
    reverb: 0.45,
    delay: 0.35,
  },
  {
    id: "deep-rhodes",
    family: "deep-house-keys",
    name: "Deep Tines",
    role: "harmony",
    alternateOnly: true,
    flavour: "mellow",
    params: p("fm", 60, {
      cutoff: 0.12,
      resonance: 0.05,
      env_amount: 0.3,
      env_decay: 1100,
      tone: 0.17,
      detune: 0.55,
      drive: 0,
      level: 0.779,
    }),
    env: env(1, 2200, 0.3, 500),
    mode: "gate",
    reverb: 0.35,
    delay: 0.2,
  },
  {
    id: "lush-pad",
    family: "deep-house-keys",
    name: "Lush Pad",
    role: "harmony",
    alternateOnly: true,
    flavour: "soft",
    params: p("poly", 60, {
      cutoff: 0.36,
      resonance: 0.05,
      env_amount: 0.15,
      env_decay: 1400,
      tone: 0.3,
      detune: 0.8,
      drive: 0,
      level: 1.523,
    }),
    env: env(250, 1400, 0.85, 1200),
    mode: "gate",
    reverb: 0.6,
    delay: 0.15,
  },
  // ---------------------------------------------------------------- Harmony: happy house
  // The bright piano stab of 90s happy house, a short supersaw stab and the classic house organ.
  {
    id: "rave-piano",
    family: "happy-house",
    name: "House Piano",
    role: "harmony",
    flavour: "euphoric",
    params: p("piano", 60, { cutoff: 0.78, tone: 0.72, env_decay: 1600, detune: 0.45, level: 3.237 }),
    env: env(1, 1800, 0.5, 300),
    mode: "gate",
    reverb: 0.35,
    delay: 0.1,
  },
  {
    id: "happy-stab",
    family: "happy-house",
    name: "Happy Stab",
    role: "harmony",
    alternateOnly: true,
    flavour: "bright",
    params: p("super", 60, {
      cutoff: 0.45,
      resonance: 0.1,
      env_amount: 0.4,
      env_decay: 250,
      detune: 0.45,
      tone: 0.5,
      drive: 0.1,
      level: 2.349,
    }),
    env: env(1, 400, 0.2, 200),
    mode: "gate",
    reverb: 0.3,
    delay: 0.15,
  },
  {
    id: "house-organ",
    family: "happy-house",
    name: "House Organ",
    role: "harmony",
    alternateOnly: true,
    flavour: "bouncy",
    params: p("organ", 60, { cutoff: 0.15, resonance: 0.55, tone: 0.4, detune: 0.3, drive: 0.15, level: 0.921 }),
    env: env(2, 300, 0.6, 120),
    mode: "gate",
    reverb: 0.3,
    delay: 0.1,
  },
  // ---------------------------------------------------------------- Harmony: afro keys
  // Warm and round: a tine e-piano with chorus, a smoky highlife organ and a soft-mallet
  // vibraphone (real modal bars, not thin FM bells).
  {
    id: "highlife-keys",
    family: "afro-keys",
    name: "Highlife Keys",
    role: "harmony",
    flavour: "warm",
    params: p("fm", 60, {
      cutoff: 0.1,
      resonance: 0.05,
      env_amount: 0.32,
      env_decay: 900,
      tone: 0.17,
      detune: 0.5,
      drive: 0,
      level: 0.8,
    }),
    env: env(1, 2000, 0.3, 450),
    mode: "gate",
    reverb: 0.3,
    delay: 0.15,
  },
  {
    id: "highlife-organ",
    family: "afro-keys",
    name: "Highlife Organ",
    role: "harmony",
    alternateOnly: true,
    flavour: "smoky",
    // Drawbar organ, jazz registration, a slow rotor and a touch of percussion.
    params: p("organ", 60, { cutoff: 0.08, resonance: 0.3, tone: 0, detune: 0.4, drive: 0.1, level: 0.833 }),
    env: env(3, 200, 1, 90),
    mode: "gate",
    reverb: 0.3,
    delay: 0.05,
  },
  {
    id: "warm-vibes",
    family: "afro-keys",
    name: "Warm Vibes",
    role: "harmony",
    alternateOnly: true,
    flavour: "velvety",
    params: p("mallet", 60, {
      tone: 0.25,
      cutoff: 0.3,
      env_decay: 1800,
      resonance: 0.35,
      detune: 0.35,
      drive: 0,
      level: 1,
    }),
    env: env(1, 2200, 1, 700),
    mode: "gate",
    reverb: 0.4,
    delay: 0.1,
  },
  // ---------------------------------------------------------------- Lead: 70s monosynth
  // A fat, warm monosynth lead with a little glide on legato notes, a two-oscillator duo lead
  // and a darker, rounder one.
  {
    id: "seventies-lead",
    family: "seventies-lead",
    name: "Seventies Lead",
    role: "lead",
    flavour: "warm",
    params: p("mono", 60, {
      cutoff: 0.48,
      resonance: 0.25,
      env_amount: 0.35,
      env_decay: 500,
      tone: 0.2,
      detune: 0.35,
      drive: 0.15,
      glide_ms: 55,
      level: 1.999,
    }),
    env: env(6, 500, 0.85, 220),
    mode: "gate",
    reverb: 0.3,
    delay: 0.25,
  },
  {
    id: "duo-lead",
    family: "seventies-lead",
    name: "Duo Lead",
    role: "lead",
    alternateOnly: true,
    flavour: "hollow",
    params: p("mono", 60, {
      cutoff: 0.45,
      resonance: 0.2,
      env_amount: 0.3,
      env_decay: 400,
      tone: 0.75,
      detune: 0.6,
      drive: 0.1,
      glide_ms: 45,
      level: 1.133,
    }),
    env: env(6, 450, 0.85, 200),
    mode: "gate",
    reverb: 0.3,
    delay: 0.25,
  },
  {
    id: "round-lead",
    family: "seventies-lead",
    name: "Round Lead",
    role: "lead",
    alternateOnly: true,
    flavour: "round",
    params: p("mono", 60, {
      cutoff: 0.36,
      resonance: 0.15,
      env_amount: 0.25,
      env_decay: 600,
      tone: 0.45,
      detune: 0.25,
      drive: 0.1,
      glide_ms: 60,
      level: 2.519,
    }),
    env: env(10, 600, 0.9, 260),
    mode: "gate",
    reverb: 0.35,
    delay: 0.2,
  },
  // ---------------------------------------------------------------- Lead: minimal
  // The minimal palette: a short, round sine blip with echo, a muted string click and a vocal chop.
  {
    id: "minimal-blip",
    family: "minimal-lead",
    name: "Minimal Blip",
    role: "lead",
    flavour: "clicky",
    params: p("fm", 60, {
      cutoff: 0.06,
      resonance: 0,
      env_amount: 0.25,
      env_decay: 60,
      tone: 0.17,
      detune: 0.15,
      drive: 0,
      level: 2.845,
    }),
    env: env(1, 220, 0, 120),
    mode: "gate",
    reverb: 0.25,
    delay: 0.45,
  },
  {
    id: "click-pluck",
    family: "minimal-lead",
    name: "Click Pluck",
    role: "lead",
    alternateOnly: true,
    flavour: "dry",
    params: p("pluck", 60, {
      cutoff: 0.55,
      resonance: 0.3,
      tone: 0.4,
      env_decay: 350,
      detune: 0.4,
      drive: 0,
      level: 2.982,
    }),
    env: env(1, 500, 0, 150),
    mode: "gate",
    reverb: 0.25,
    delay: 0.4,
  },
  {
    id: "chop-vox",
    family: "minimal-lead",
    name: "Vox Chop",
    role: "lead",
    alternateOnly: true,
    flavour: "vocal",
    params: p("choir", 60, { cutoff: 0.5, env_amount: 0.4, tone: 0.5, detune: 0.3, drive: 0.1, level: 3.021 }),
    env: env(4, 260, 0.2, 140),
    mode: "gate",
    reverb: 0.3,
    delay: 0.4,
  },
  // ---------------------------------------------------------------- Bass: 70s monosynth
  {
    id: "fat-mono-bass",
    family: "seventies-bass",
    name: "Fat Mono Bass",
    role: "bass",
    flavour: "fat",
    params: p("mono", 36, {
      cutoff: 0.3,
      resonance: 0.2,
      env_amount: 0.4,
      env_decay: 260,
      tone: 0.35,
      detune: 0.3,
      drive: 0.15,
      glide_ms: 40,
      level: 3.422,
    }),
    env: env(2, 300, 0.75, 90),
    mode: "gate",
    reverb: 0,
    delay: 0,
  },
  {
    id: "funk-synth-bass",
    family: "seventies-bass",
    name: "Funk Synth Bass",
    role: "bass",
    alternateOnly: true,
    flavour: "funky",
    params: p("mono", 36, {
      cutoff: 0.26,
      resonance: 0.38,
      env_amount: 0.6,
      env_decay: 160,
      tone: 0.2,
      detune: 0.2,
      drive: 0.15,
      glide_ms: 30,
      level: 3.328,
    }),
    env: env(1, 250, 0.6, 70),
    mode: "gate",
    reverb: 0,
    delay: 0,
  },
  {
    id: "round-mono-bass",
    family: "seventies-bass",
    name: "Round Mono Bass",
    role: "bass",
    alternateOnly: true,
    flavour: "round",
    params: p("mono", 36, {
      cutoff: 0.22,
      resonance: 0.1,
      env_amount: 0.25,
      env_decay: 300,
      tone: 0.6,
      detune: 0.15,
      drive: 0.1,
      glide_ms: 40,
      level: 3.219,
    }),
    env: env(2, 350, 0.8, 90),
    mode: "gate",
    reverb: 0,
    delay: 0,
  },
  // ---------------------------------------------------------------- Bass: tech house
  // The rubbery, plucky tech house bass and its cousins: a filtered pluck and a square sub stab.
  {
    id: "rubber-bass",
    family: "tech-bass",
    name: "Rubber Bass",
    role: "bass",
    flavour: "rubbery",
    params: p("mono", 36, {
      cutoff: 0.24,
      resonance: 0.42,
      env_amount: 0.55,
      env_decay: 120,
      tone: 0.1,
      detune: 0.1,
      drive: 0.2,
      level: 3.638,
    }),
    env: env(1, 200, 0.25, 60),
    mode: "gate",
    reverb: 0,
    delay: 0,
  },
  {
    id: "tech-pluck-bass",
    family: "tech-bass",
    name: "Tech Pluck Bass",
    role: "bass",
    alternateOnly: true,
    flavour: "tight",
    params: p("acid", 36, {
      cutoff: 0.2,
      resonance: 0.3,
      env_amount: 0.5,
      env_decay: 140,
      tone: 0.3,
      drive: 0.2,
      level: 3.288,
    }),
    env: env(1, 220, 0.2, 60),
    mode: "gate",
    reverb: 0,
    delay: 0,
  },
  {
    id: "square-sub-stab",
    family: "tech-bass",
    name: "Square Sub",
    role: "bass",
    alternateOnly: true,
    flavour: "hollow",
    params: p("mono", 36, {
      cutoff: 0.18,
      resonance: 0.15,
      env_amount: 0.35,
      env_decay: 150,
      tone: 0.9,
      detune: 0.05,
      drive: 0.15,
      level: 2.961,
    }),
    env: env(1, 220, 0.4, 60),
    mode: "gate",
    reverb: 0,
    delay: 0,
  },
  // ---------------------------------------------------------------- Classics: cellos
  {
    id: "solo-cello",
    family: "cellos",
    name: "Solo Cello",
    role: "classic",
    classic: true,
    flavour: "tender",
    params: p("bowed", 48, {
      cutoff: 0.48,
      tone: 0.75,
      detune: 0,
      env_amount: 0.55,
      env_decay: 260,
      drive: 0.15,
      level: 1.389,
    }),
    env: env(90, 400, 0.9, 350),
    mode: "gate",
    reverb: 0.4,
    delay: 0.05,
  },
  {
    id: "cello-section",
    family: "cellos",
    name: "Cello Section",
    role: "classic",
    classic: true,
    alternateOnly: true,
    flavour: "warm",
    params: p("bowed", 48, {
      cutoff: 0.45,
      tone: 0.7,
      detune: 0.55,
      env_amount: 0.45,
      env_decay: 350,
      drive: 0.12,
      level: 1.468,
    }),
    env: env(160, 400, 0.9, 500),
    mode: "gate",
    reverb: 0.45,
    delay: 0,
  },
  {
    id: "cello-pizzicato",
    family: "cellos",
    name: "Cello Pizzicato",
    role: "classic",
    classic: true,
    alternateOnly: true,
    flavour: "plucked",
    params: p("pluck", 48, {
      cutoff: 0.2,
      resonance: 0.45,
      tone: 0.12,
      env_decay: 650,
      detune: 0.85,
      drive: 0,
      level: 2.259,
    }),
    env: env(1, 800, 0, 250),
    mode: "gate",
    reverb: 0.35,
    delay: 0.05,
  },
  // ---------------------------------------------------------------- Classics: baroque
  {
    id: "harpsichord",
    family: "baroque",
    name: "Harpsichord",
    role: "classic",
    classic: true,
    flavour: "baroque",
    params: p("pluck", 60, {
      cutoff: 0.85,
      resonance: 0.12,
      tone: 0.75,
      env_decay: 1800,
      detune: 0.45,
      drive: 0,
      level: 1.174,
    }),
    env: env(1, 2500, 1, 200),
    mode: "gate",
    reverb: 0.35,
    delay: 0,
  },
  {
    id: "lute",
    family: "baroque",
    name: "Lute",
    role: "classic",
    classic: true,
    alternateOnly: true,
    flavour: "gentle",
    params: p("pluck", 60, {
      cutoff: 0.45,
      resonance: 0.35,
      tone: 0.45,
      env_decay: 1600,
      detune: 0.75,
      drive: 0,
      level: 1.412,
    }),
    env: env(1, 2500, 1, 250),
    mode: "gate",
    reverb: 0.35,
    delay: 0.05,
  },
  {
    id: "spinet",
    family: "baroque",
    name: "Spinet",
    role: "classic",
    classic: true,
    alternateOnly: true,
    flavour: "delicate",
    params: p("pluck", 60, {
      cutoff: 0.7,
      resonance: 0.18,
      tone: 0.6,
      env_decay: 1300,
      detune: 0.35,
      drive: 0,
      level: 1.355,
    }),
    env: env(1, 2000, 1, 180),
    mode: "gate",
    reverb: 0.3,
    delay: 0,
  },
  // ---------------------------------------------------------------- Classics: woodwinds
  {
    id: "oboe",
    family: "woodwinds",
    name: "Oboe",
    role: "classic",
    classic: true,
    flavour: "plaintive",
    params: p("reed", 72, {
      tone: 0.55,
      cutoff: 0.62,
      resonance: 0.6,
      env_amount: 0.2,
      env_decay: 120,
      drive: 0.05,
      level: 1.532,
    }),
    env: env(20, 400, 0.92, 140),
    mode: "gate",
    reverb: 0.4,
    delay: 0.05,
  },
  {
    id: "english-horn",
    family: "woodwinds",
    name: "English Horn",
    role: "classic",
    classic: true,
    alternateOnly: true,
    flavour: "dusky",
    params: p("reed", 60, {
      tone: 0.5,
      cutoff: 0.5,
      resonance: 0.55,
      env_amount: 0.2,
      env_decay: 140,
      drive: 0.05,
      level: 1.516,
    }),
    env: env(25, 400, 0.92, 160),
    mode: "gate",
    reverb: 0.4,
    delay: 0.05,
  },
  {
    id: "bassoon",
    family: "woodwinds",
    name: "Bassoon",
    role: "classic",
    classic: true,
    alternateOnly: true,
    flavour: "mellow",
    params: p("reed", 48, {
      tone: 0.4,
      cutoff: 0.4,
      resonance: 0.5,
      env_amount: 0.15,
      env_decay: 160,
      drive: 0.08,
      level: 1.511,
    }),
    env: env(25, 400, 0.92, 160),
    mode: "gate",
    reverb: 0.4,
    delay: 0.05,
  },
  // ---------------------------------------------------------------- Classics: brass (more)
  {
    id: "flugelhorn",
    family: "classic-brass",
    name: "Flugelhorn",
    role: "classic",
    classic: true,
    alternateOnly: true,
    flavour: "velvet",
    params: p("brass", 72, {
      cutoff: 0.32,
      resonance: 0.1,
      env_amount: 0.3,
      env_decay: 400,
      tone: 0,
      detune: 0,
      drive: 0.02,
      level: 1.554,
    }),
    env: env(25, 400, 0.9, 220),
    mode: "gate",
    reverb: 0.4,
    delay: 0.1,
  },
];
